import React from 'react';
import * as IoC from 'typescript-ioc';
import { FormattedMessage } from 'react-intl';
import { alertServerError } from '../../components/global/modal/alerts-host/alert-templates';
import { IErrorParsing } from '../error-parsing';
import { ConnectionType } from '../../api/connections.dao';
import { meetingWebinarText } from './connections.utils';
import { Space } from '../../constants/html-character-entities';
import { LongRunningJob } from '../../domain/jobs';
import { EventExecutionRuntime } from '../../domain/jobs/implementations/event-execution-runtime';
import { JobType } from '../../api/jobs.dao';
import { IAlertsService } from '../alerts';
import { CANCEL_TEXT } from '../../constants/common-labels.constants';

const UNIQUE_JOINING_LINKS_DELETE_JOB_STARTED_TEXT = {
    defaultMessage: `Link deletion in progress.  The Edit Meeting Connections pop up will be closed and locked while this process takes place. 
        Once the link deletion is fully complete, you will be able to continue editing connections for this meeting.`,
    id: 'oe.common.alerts.linkDeletionInProgress',
    description: 'Job description title'
};

// TODO: Move this out from utils
export class UniqueJoiningLInksAlertsPopup {

    constructor(
        private readonly meetingId: number,
        private readonly closeMeetingConnectionPopUp: () => void,
        private readonly saveDeleteUJLCompletedCallback?: () => Promise<void>,
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly jobsPipeline = IoC.Container.get(EventExecutionRuntime)
    ) {
    }

    static handleApiException(e: Error, title: string = 'Error'): void {
        if (e) alertServerError(title, IoC.Container.get(IErrorParsing).extractMessage(e));
    }

    private get removeUniqueJoiningLinksJob(): LongRunningJob {
        return this.jobsPipeline.getJobByType(JobType.DeleteUniqueJoiningLinks);
    }

    private invokeDeleteUJLJob = async (): Promise<void> => {
        const job = this.removeUniqueJoiningLinksJob;
        if (this.saveDeleteUJLCompletedCallback) job.onComplete(this.saveDeleteUJLCompletedCallback.bind(this));
        await job.trigger(
            { meetingId: this.meetingId },
            {
                description: <FormattedMessage {...UNIQUE_JOINING_LINKS_DELETE_JOB_STARTED_TEXT} />
            }
        );
    };

    private removeUniqueJoiningLinks = async (): Promise<void> => {
        try {
            await this.invokeDeleteUJLJob();
            this.closeMeetingConnectionPopUp();
        } catch (error) {
            // handling error on higher level
        }
    };

    public handleRemoveUniqueJoiningLinks = (uniqueJoiningLinksQuantity: number, connectionType: ConnectionType) => {
        if (!uniqueJoiningLinksQuantity) return;
        this.alertsService.confirm({
            message: (
                <span className='alert-message'>
                    <FormattedMessage
                        id='oe.alerts.thereAreUJLConnectionsOnTheMeetingExist'
                        description='Alert message'
                        defaultMessage='There are {UJLQuantity} Unique Joining Link connections on the meeting. These connections will not work now that the associated Zoom {meetingOrWebinar} has been removed'
                        values={{
                            UJLQuantity: uniqueJoiningLinksQuantity,
                            meetingOrWebinar: meetingWebinarText[connectionType]
                        }}
                    />
                    <div className='m-top-10px m-bottom-10px'></div>
                    <FormattedMessage
                        description='Text message'
                        defaultMessage='You should'
                        id='oe.warnings.youShould'
                    />
                    <Space />
                    <span className='alert-message--danger'>
                        <FormattedMessage
                            id='oe.alerts.deleteThisLinks'
                            description='Alert message'
                            defaultMessage='delete these links'
                        />
                    </span>
                    <Space />
                    <FormattedMessage
                        description='Text message'
                        defaultMessage='so that the attendees can receive fresh links for any new connections added to the meeting.'
                        id='oe.warnings.attendeesCanReceiveFreshLinks'
                    />
                </span>
            ),
            confirmButtonProps: {
                text: (
                    <FormattedMessage
                        id='oe.common.deleteLinks'
                        description='Button label'
                        defaultMessage='Delete Links'
                    />
                ),
                modifier: 'confirm-warn',
                handler: this.removeUniqueJoiningLinks
            },
            cancelButtonProps: {
                text: (
                    <FormattedMessage {...CANCEL_TEXT} />
                ),
                modifier: 'cancel'
            }
        });
    };

    public handleRemoveUniqueJoiningLinksInBatch = () => {
        this.alertsService.confirm({
            message: (
                <span className='alert-message'>
                    <FormattedMessage
                        id='oe.confirm.areYouSureTo'
                        description='Confirm message'
                        defaultMessage='Are you sure you want to'
                    />
                    <Space />
                    <span className='alert-message--danger'>
                        <FormattedMessage
                            id='oe.accountGroup.deleteAllUniqueJoiningLinks'
                            description='Confirm message'
                            defaultMessage='Delete all Unique Joining Links'
                        />
                    </span>
                    ?
                    <Space />
                    <FormattedMessage
                        id='oe.blueprints.attendeeWillNotBeAbleToConnectUsingTheirLinks'
                        description='Alert message'
                        defaultMessage='Attendees will no longer be able to connect to the meeting using their unique links.'
                    />
                </span>
            ),
            confirmButtonProps: {
                text: (
                    <FormattedMessage
                        id='oe.common.deleteLinks'
                        description='Button label'
                        defaultMessage='Delete Links'
                    />
                ),
                modifier: 'confirm-warn',
                handler: this.removeUniqueJoiningLinks
            },
            cancelButtonProps: {
                text: (<FormattedMessage {...CANCEL_TEXT} />),
                modifier: 'cancel'
            }
        });
    };
}